"use client";

/**
 * React pieces of an OTA update, presentation-neutral (`classNames` for hosts with a CSS
 * system, `styles` for inline-styled hosts). Behaviour stays on the host through callbacks:
 * the host's backend owns the WatchPOD client; these components only call it.
 *
 *   <UpdateAvailableModal/>  the one moment that asks a human: a release exists — now or later
 *   <UpdatingOverlay/>       full-screen wait while the container is recreated
 *   <ReleaseNotesModal/>     "what changed" for the running version (CHANGELOG), with an
 *                            optional «Controlla aggiornamenti» that hands a found release up
 *   <UpdatesCard/>           settings read-out: version, pairing, offer, «scollega e richiedi di nuovo»
 *   <UpdateCountdownToast/>  «si aggiornerà tra 4:59», top centre, while an update is announced
 *   <UpdateNoticeLayer/>     the whole announced-update flow for every page: countdown toast,
 *                            then the update screen, then the reload onto the new version
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Dropdown } from "./Dropdown";
import {
  parseNotesBody,
  parseReleaseNotes,
  reloadOntoNewVersion,
  runningVersion,
  waitForRestart,
  watchUpdateNotice,
  type BrowserUpdateOptions,
  type ReleaseNotes,
  type ReleaseNotesGroup,
} from "../browser";
import type { PairingStatus, UpdateNotice, UpdateOverview } from "../core/types";

export type UpdateOffer = { version: string; notes: string; releasedAt: string };

export const PAIRING_LABEL: Record<PairingStatus, string> = {
  not_configured: "aggiornamenti non attivi",
  pending: "in attesa di approvazione",
  active: "collegato",
  unpaired: "token mancante",
  rejected: "collegamento rifiutato",
  error: "non raggiungibile",
};

export type UpdateActions = {
  /** POST install on the host → WatchPOD; resolves with the accepted version. */
  install: (version: string) => Promise<{ version: string }>;
  /** Waits for the new container (host wires `waitForRestart`). */
  waitForRestart: (version: string, onTick: (s: number) => void) => Promise<boolean>;
  /** Cache-busting reload (host wires `reloadOntoNewVersion`). */
  reload: () => Promise<void> | void;
  snooze?: (version: string) => Promise<unknown>;
};

type StyleKeys =
  | "overlay"
  | "modal"
  | "title"
  | "body"
  | "notes"
  | "footer"
  | "button"
  | "buttonPrimary"
  | "input"
  | "muted"
  | "error"
  | "section"
  | "sectionTitle"
  | "row"
  | "rowTitle"
  | "select"
  | "menu"
  | "toggle"
  | "toggleOn"
  | "knob"
  | "toast"
  | "toastButton";

export type UiProps = {
  appName: string;
  classNames?: Partial<Record<StyleKeys, string>>;
  styles?: Partial<Record<StyleKeys, CSSProperties>>;
  formatDate?: (iso: string) => string;
  /**
   * The host's own "update in progress" screen. When set, every wait in the package —
   * the settings card, the update modal and the announced update on other pages — draws
   * this, so a manual install and an automatic one look the same.
   */
  renderUpdating?: (state: UpdatingState) => ReactNode;
};

const base: Record<StyleKeys, CSSProperties> = {
  toast: {
    position: "fixed",
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "12px 18px",
    maxWidth: "calc(100vw - 32px)",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 12,
    boxShadow: "0 18px 50px -18px rgba(0,0,0,.55)",
    fontFamily: "inherit",
  },
  toastButton: {
    flexShrink: 0,
    padding: "7px 14px",
    border: "none",
    borderRadius: 8,
    background: "#fff",
    color: "#0f172a",
    fontWeight: 700,
    fontSize: 13,
    fontFamily: "inherit",
    cursor: "pointer",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(4,7,12,.5)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  modal: {
    width: "100%",
    maxWidth: 560,
    background: "#fff",
    color: "#0f172a",
    borderRadius: 16,
    boxShadow: "0 40px 100px -30px rgba(0,0,0,.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "inherit",
  },
  title: { fontSize: 18, fontWeight: 700, margin: 0 },
  body: {
    padding: "18px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxHeight: "60vh",
    overflowY: "auto",
  },
  notes: {
    border: "1px solid rgba(15,23,42,.1)",
    borderRadius: 12,
    padding: "12px 16px",
    fontSize: 13,
    lineHeight: 1.55,
    whiteSpace: "pre-line",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 24px",
    borderTop: "1px solid rgba(15,23,42,.08)",
  },
  button: {
    border: "1px solid rgba(15,23,42,.15)",
    background: "transparent",
    color: "inherit",
    borderRadius: 10,
    padding: "9px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    font: "inherit",
  },
  buttonPrimary: {
    border: "none",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 10,
    padding: "9px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    font: "inherit",
  },
  input: {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,.15)",
    font: "inherit",
    color: "inherit",
    background: "transparent",
  },
  muted: { fontSize: 13, opacity: 0.7, lineHeight: 1.5 },
  error: { fontSize: 13, color: "#ef4444" },
  section: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    width: "100%",
    padding: "18px 20px",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 14,
  },
  sectionTitle: { fontSize: 15, fontWeight: 700 },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: "14px 0",
    borderTop: "1px solid rgba(15,23,42,.12)",
  },
  rowTitle: { fontSize: "13.5px", fontWeight: 600 },
  menu: {},
  select: {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,.15)",
    font: "inherit",
    color: "inherit",
    background: "transparent",
    minWidth: 190,
  },
  toggle: {
    width: 44,
    height: 25,
    borderRadius: 999,
    border: "none",
    padding: 0,
    position: "relative",
    background: "rgba(15,23,42,.18)",
    cursor: "pointer",
  },
  toggleOn: { background: "#2563eb" },
  knob: {
    position: "absolute",
    top: 3,
    width: 19,
    height: 19,
    borderRadius: "50%",
    background: "#fff",
    transition: "left .15s ease",
  },
};

const useS = (ui: UiProps) => (k: StyleKeys) => ({ ...base[k], ...(ui.styles?.[k] ?? {}) });
const defaultDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.valueOf())
    ? iso
    : d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
};

// ---- UpdatingOverlay ----------------------------------------------------------------------------

export function UpdatingOverlay({
  ui,
  version,
  elapsed,
  onReload,
}: {
  ui: UiProps;
  version: string;
  elapsed: number;
  onReload: () => void;
}) {
  const s = useS(ui);
  if (ui.renderUpdating) {
    return <>{ui.renderUpdating({ version, elapsed, reload: onReload })}</>;
  }
  return (
    <div
      className={ui.classNames?.overlay}
      style={{
        ...s("overlay"),
        flexDirection: "column",
        gap: 18,
        textAlign: "center",
        background: s("modal").background,
        color: s("modal").color,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: "4px solid currentColor",
          borderTopColor: "transparent",
          opacity: 0.6,
          animation: "wp-spin 1s linear infinite",
        }}
      />
      <style>{"@keyframes wp-spin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ fontSize: 20, fontWeight: 700 }}>Aggiornamento in corso</div>
      <div style={{ ...s("muted"), maxWidth: 420 }}>
        {version
          ? `${ui.appName} si sta riavviando sulla versione ${version}. `
          : `${ui.appName} si sta riavviando. `}
        La pagina si ricarica da sola appena l'applicazione risponde di nuovo.
      </div>
      <div style={{ fontSize: 12, opacity: 0.55 }}>
        {elapsed > 0 ? `In attesa da ${elapsed}s` : "Avvio…"}
      </div>
      {elapsed >= 15 && (
        <button
          type="button"
          className={ui.classNames?.button}
          style={s("button")}
          onClick={onReload}
        >
          Ricarica ora
        </button>
      )}
    </div>
  );
}

// ---- Announced update: countdown, then wait --------------------------------------------------

/** The update the host announces on `/health`, refreshed every `intervalMs` (default 5 s). */
export function useUpdateNotice(
  options: BrowserUpdateOptions | null,
  intervalMs = NOTICE_POLL_MS,
): UpdateNotice | null {
  const [notice, setNotice] = useState<UpdateNotice | null>(null);
  const healthUrl = options?.healthUrl;
  useEffect(() => {
    if (!options || !healthUrl) return;
    return watchUpdateNotice(options, setNotice, intervalMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthUrl, intervalMs]);
  return notice;
}

/** How often pages read `/health` for an announced update: short, so a quick install is not missed. */
export const NOTICE_POLL_MS = 5_000;

const clock = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * «Helios si aggiornerà alla 1.4.0 tra 4:59», top centre, while an update is announced.
 * Shows nothing for any other phase: when the update starts, the update screen takes over.
 */
/**
 * A short two-note chime, made with Web Audio (no file to ship). Best effort: a browser
 * that blocks audio before any click on the page just stays silent.
 */
export function playUpdateChime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const start = ctx.currentTime;
    [880, 1318.5].forEach((frequency, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = frequency;
      const at = start + i * 0.18;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.18, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.4);
    });
    setTimeout(() => void ctx.close().catch(() => undefined), 1_000);
  } catch {
    // no audio: the toast is still there
  }
}

export function UpdateCountdownToast({
  ui,
  notice,
  onInstallNow,
  sound = true,
}: {
  ui: UiProps;
  notice: UpdateNotice | null;
  /**
   * «Aggiorna ora»: starts the announced install right away. Pass it only to whoever may
   * install (the host's permission); without it the toast just counts down.
   */
  onInstallNow?: () => Promise<unknown>;
  /**
   * Beep when the countdown appears and then every minute. Default on; the host's
   * setting travels in `notice.sound`, this prop can only silence it further.
   */
  sound?: boolean;
}) {
  const s = useS(ui);
  const [now, setNow] = useState(() => Date.now());
  const beeping = sound && notice?.phase === "scheduled" && notice.sound !== false;
  // The minute the countdown is in: a beep when the toast appears, then one per minute.
  const minuteMark =
    beeping && notice ? Math.ceil((new Date(notice.at).getTime() - now) / 60_000) : 0;
  useEffect(() => {
    if (minuteMark > 0) playUpdateChime();
  }, [minuteMark]);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);
  const scheduled = notice?.phase === "scheduled";
  useEffect(() => {
    if (!scheduled) return;
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [scheduled]);
  if (!notice || !scheduled) return null;
  const left = new Date(notice.at).getTime() - now;
  const installNow = onInstallNow
    ? () => {
        setStarting(true);
        setFailed(false);
        onInstallNow().then(
          () => setStarting(false),
          () => {
            setStarting(false);
            setFailed(true);
          },
        );
      }
    : undefined;
  return (
    <div role="status" aria-live="polite" className={ui.classNames?.toast} style={s("toast")}>
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: "#f59e0b",
          flexShrink: 0,
        }}
      />
      <div>
        <div style={{ fontWeight: 700 }}>
          {left > 0
            ? `${ui.appName} si aggiornerà alla versione ${notice.version} tra ${clock(left)}`
            : `${ui.appName} si sta aggiornando alla versione ${notice.version}…`}
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.75 }}>
          {failed
            ? "L'aggiornamento non è partito: riprova, o attendi la fine del conto alla rovescia."
            : "Concludi quello che stai facendo: per circa un minuto l'applicazione non risponderà, poi la pagina si ricarica da sola."}
        </div>
      </div>
      {installNow && left > 0 ? (
        <button
          type="button"
          className={ui.classNames?.toastButton}
          style={{ ...s("toastButton"), opacity: starting ? 0.7 : 1 }}
          disabled={starting}
          onClick={installNow}
        >
          {starting ? "Avvio…" : "Aggiorna ora"}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The whole announced-update flow, for every page of the app (not only administrators):
 * the countdown toast while the update is `scheduled`, then — once it is `installing` —
 * the update screen, which waits for the new version and reloads onto it. Mount it once.
 */
/** What a host-drawn "update in progress" screen gets from `<UpdateNoticeLayer/>`. */
export interface UpdatingState {
  /** The version being installed. */
  version: string;
  /** Seconds this page has been waiting for the server to come back. */
  elapsed: number;
  /** Reloads onto the new version now (the "reload" button). */
  reload: () => void;
}

export function UpdateNoticeLayer({
  ui,
  options,
  appVersion,
  intervalMs,
  installNow,
  renderUpdating,
}: {
  ui: UiProps;
  options: BrowserUpdateOptions;
  /** The version this page was built from: the reload waits for a different one. */
  appVersion: string;
  intervalMs?: number;
  /**
   * Starts the announced install now (the host's install endpoint). Shows «Aggiorna ora»
   * on the toast; leave it out for users who may not install.
   */
  installNow?: (version: string) => Promise<unknown>;
  /**
   * Draws the "update in progress" screen in the host's own style, instead of the built-in
   * `UpdatingOverlay`. Gets the version being installed, the seconds already waited and the
   * reload action; the wait-and-reload logic stays here.
   */
  renderUpdating?: (state: UpdatingState) => ReactNode;
}) {
  const polled = useUpdateNotice(options, intervalMs);
  // After «Aggiorna ora» this page goes to the update screen at once, without waiting
  // for the next poll; the other pages learn it from /health.
  const [startedHere, setStartedHere] = useState<UpdateNotice | null>(null);
  // A page that missed the whole install (it was hidden, or the update was quick) finds
  // the server already on another version: it goes to the same wait-and-reload path.
  const [movedOn, setMovedOn] = useState<UpdateNotice | null>(null);
  useEffect(() => {
    if (!options) return;
    let alive = true;
    const check = async () => {
      const running = await runningVersion(options);
      if (alive && running && appVersion && running !== appVersion) {
        setMovedOn({ version: running, at: new Date().toISOString(), phase: "installing" });
      }
    };
    const timer = setInterval(() => void check(), intervalMs ?? NOTICE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appVersion, intervalMs]);
  const notice = startedHere ?? polled ?? movedOn;
  const [elapsed, setElapsed] = useState(0);
  const installing = notice?.phase === "installing" ? notice.version : "";
  useEffect(() => {
    if (!installing) return;
    let alive = true;
    void waitForRestart(options, installing, appVersion, (seconds) => {
      if (alive) setElapsed(seconds);
    }).then((back) => {
      if (alive && back) void reloadOntoNewVersion(options);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installing, appVersion]);
  if (installing) {
    const reload = () => void reloadOntoNewVersion(options);
    if (renderUpdating) {
      return <>{renderUpdating({ version: installing, elapsed, reload })}</>;
    }
    return (
      <UpdatingOverlay
        ui={ui}
        version={installing}
        elapsed={elapsed}
        onReload={() => void reloadOntoNewVersion(options)}
      />
    );
  }
  const version = notice?.version;
  return (
    <UpdateCountdownToast
      ui={ui}
      notice={notice}
      onInstallNow={
        installNow && version
          ? async () => {
              await installNow(version);
              setStartedHere({ version, at: new Date().toISOString(), phase: "installing" });
            }
          : undefined
      }
    />
  );
}

// ---- UpdateAvailableModal ----------------------------------------------------------------------

export function UpdateAvailableModal({
  ui,
  offer,
  actions,
  onClose,
  onHandled,
  warning,
}: {
  ui: UiProps;
  offer: UpdateOffer;
  actions: UpdateActions;
  onClose: () => void;
  /** Called after «Posticipa» so the host can retire its notice. */
  onHandled?: () => void;
  /** Host-specific sentence, e.g. "Prima di partire viene salvata una copia dei dati." */
  warning?: ReactNode;
}) {
  const s = useS(ui);
  const fmt = ui.formatDate ?? defaultDate;
  // The release's notes read like the CHANGELOG in the «Novità» window, not as raw text.
  const offerGroups = parseNotesBody(offer.notes || "");
  const [busy, setBusy] = useState(false);
  const [restarting, setRestarting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const install = async () => {
    setBusy(true);
    setError(null);
    try {
      const accepted = await actions.install(offer.version);
      setRestarting(0);
      const back = await actions.waitForRestart(accepted.version || offer.version, setRestarting);
      if (back) {
        await actions.reload();
        return;
      }
      setRestarting(null);
      setError("Il riavvio sta impiegando più del previsto: controlla lo storico su WatchPOD.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aggiornamento non riuscito.");
    } finally {
      setBusy(false);
    }
  };
  const postpone = async () => {
    setBusy(true);
    try {
      await actions.snooze?.(offer.version);
      onHandled?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Non è stato possibile rimandare.");
    } finally {
      setBusy(false);
    }
  };

  if (restarting !== null) {
    return (
      <UpdatingOverlay
        ui={ui}
        version={offer.version}
        elapsed={restarting}
        onReload={() => void actions.reload()}
      />
    );
  }
  return (
    <div
      className={ui.classNames?.overlay}
      style={s("overlay")}
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <section className={ui.classNames?.modal} style={s("modal")}>
        <header style={{ padding: "18px 24px 0" }}>
          <h3 className={ui.classNames?.title} style={s("title")}>
            Aggiornamento disponibile
          </h3>
        </header>
        <div className={ui.classNames?.body} style={s("body")}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {ui.appName} {offer.version}
            {offer.releasedAt && (
              <span style={{ fontWeight: 400, opacity: 0.7 }}>
                {" "}
                — rilasciata il {fmt(offer.releasedAt)}
              </span>
            )}
          </div>
          {offerGroups.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                maxHeight: "45vh",
                overflowY: "auto",
              }}
            >
              <NotesGroups groups={offerGroups} />
            </div>
          )}
          <div className={ui.classNames?.muted} style={s("muted")}>
            L'app si riavvia per aggiornarsi: circa un minuto di indisponibilità. {warning}
          </div>
          {error && (
            <div className={ui.classNames?.error} style={s("error")}>
              {error}
            </div>
          )}
        </div>
        <footer className={ui.classNames?.footer} style={s("footer")}>
          <button
            type="button"
            className={ui.classNames?.button}
            style={s("button")}
            disabled={busy}
            onClick={() => void postpone()}
          >
            Posticipa
          </button>
          <button
            type="button"
            className={ui.classNames?.buttonPrimary}
            style={s("buttonPrimary")}
            disabled={busy}
            onClick={() => void install()}
          >
            {busy ? "Aggiornamento…" : "Aggiorna ora"}
          </button>
        </footer>
      </section>
    </div>
  );
}

// ---- ReleaseNotesModal -------------------------------------------------------------------------

/** Markdown emphasis without its markers. */
function withEmphasis(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`"))
      return (
        <code key={i} style={{ fontSize: 12 }}>
          {part.slice(1, -1)}
        </code>
      );
    return part;
  });
}

/**
 * Release notes as groups and bullets, the same everywhere: the «Novità» window, the
 * «Aggiornamento disponibile» modal and the settings card. Every item, none cut off;
 * sub-items one level in.
 */
export function NotesGroups({ groups }: { groups: ReleaseNotesGroup[] }) {
  return (
    <>
      {groups.map((g) => (
        <section key={g.title} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{g.title}</h4>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 13,
              lineHeight: 1.55,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {g.items.map((item, i) => (
              <li
                key={i}
                style={g.levels?.[i] ? { marginLeft: 18, listStyleType: "circle" } : undefined}
              >
                {withEmphasis(item)}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

type CheckResult =
  { kind: "current" } | { kind: "update"; latest: string } | { kind: "error"; message: string };

export function ReleaseNotesModal({
  ui,
  changelog,
  appVersion,
  onClose,
  check,
  onUpdateFound,
  onInstall,
  eyebrow = "Aggiornamento completato",
}: {
  ui: UiProps;
  /** Full CHANGELOG.md text (inject at build time, e.g. Vite `define`). */
  changelog: string;
  appVersion: string;
  onClose: () => void;
  /** When given, shows «Controlla aggiornamenti» (host GET overview). */
  check?: () => Promise<UpdateOverview>;
  onUpdateFound?: (offer: UpdateOffer) => void;
  /** Opens the install flow for a found release. */
  onInstall?: () => void;
  eyebrow?: string;
}) {
  const s = useS(ui);
  const fmt = ui.formatDate ?? defaultDate;
  const notes: ReleaseNotes = parseReleaseNotes(changelog, appVersion);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);

  const runCheck = async () => {
    if (!check) return;
    setChecking(true);
    setResult(null);
    try {
      const o = await check();
      if (o.updateAvailable && o.latest) {
        setResult({ kind: "update", latest: o.latest });
        onUpdateFound?.({ version: o.latest, notes: o.notes, releasedAt: o.releasedAt });
      } else if (o.pairing !== "active") {
        setResult({
          kind: "error",
          message: `WatchPOD: ${PAIRING_LABEL[o.pairing]}${o.lastError ? ` — ${o.lastError}` : ""}.`,
        });
      } else {
        setResult({ kind: "current" });
      }
    } catch (e) {
      setResult({
        kind: "error",
        message: e instanceof Error ? e.message : "Controllo non riuscito.",
      });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className={ui.classNames?.overlay}
      style={s("overlay")}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <section className={ui.classNames?.modal} style={s("modal")}>
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            padding: "18px 24px",
            borderBottom: "1px solid rgba(15,23,42,.08)",
          }}
        >
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                opacity: 0.7,
              }}
            >
              {eyebrow}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h3 className={ui.classNames?.title} style={s("title")}>
                {ui.appName} {notes.version}
              </h3>
              {result?.kind === "current" && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 9px",
                    borderRadius: 999,
                    background: "rgba(16,185,129,.15)",
                    color: "#059669",
                  }}
                >
                  Ultima versione
                </span>
              )}
              {result?.kind === "update" && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 9px",
                    borderRadius: 999,
                    background: "rgba(245,158,11,.15)",
                    color: "#b45309",
                  }}
                >
                  Aggiornamento disponibile
                </span>
              )}
            </div>
            {notes.date && <div style={{ fontSize: 12, opacity: 0.6 }}>{fmt(notes.date)}</div>}
            {result?.kind === "error" && (
              <div className={ui.classNames?.error} style={{ ...s("error"), marginTop: 6 }}>
                {result.message}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 24,
              lineHeight: 1,
              cursor: "pointer",
              opacity: 0.5,
              color: "inherit",
            }}
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div className={ui.classNames?.body} style={s("body")}>
          {notes.groups.length === 0 && (
            <p className={ui.classNames?.muted} style={s("muted")}>
              Nessuna nota per questa versione.
            </p>
          )}
          <NotesGroups groups={notes.groups} />
        </div>
        <footer
          className={ui.classNames?.footer}
          style={{ ...s("footer"), justifyContent: "center" }}
        >
          {check ? (
            <button
              type="button"
              className={ui.classNames?.buttonPrimary}
              style={s("buttonPrimary")}
              disabled={checking}
              onClick={() => (result?.kind === "update" ? onInstall?.() : void runCheck())}
            >
              {checking
                ? "Controllo in corso…"
                : result?.kind === "update"
                  ? `Aggiorna alla ${result.latest}`
                  : "Controlla aggiornamenti"}
            </button>
          ) : (
            <button
              type="button"
              className={ui.classNames?.buttonPrimary}
              style={s("buttonPrimary")}
              onClick={onClose}
            >
              Continua
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}

// ---- UpdatesCard ---------------------------------------------------------------------------------

export function UpdatesCard({
  ui,
  overview,
  actions,
  onSaveToken,
  onSaveUrl,
  onRetry,
  onUnpair,
  onRefresh,
  envHint = "WATCHPOD_URL",
}: {
  ui: UiProps;
  overview: UpdateOverview | null;
  actions: UpdateActions;
  /** Recovery path when the token is no longer accepted. */
  onSaveToken?: (token: string) => Promise<unknown>;
  /**
   * Salva l'indirizzo di WatchPOD e fa partire la richiesta di collegamento.
   *
   * Quando c'e', un host senza `WATCHPOD_URL` nello stack chiede l'indirizzo qui invece di
   * limitarsi a dire che manca: digitare un indirizzo non e' un privilegio — il confine di
   * fiducia resta l'approvazione sul pannello di WatchPOD, dove una persona conferma repo,
   * stack e immagine.
   */
  onSaveUrl?: (url: string) => Promise<unknown>;
  /**
   * Ripresentarsi dopo un rifiuto.
   *
   * Senza, un'app rifiutata resta ferma per sempre: il client non insiste — giustamente,
   * un rifiuto è una decisione — e con l'indirizzo fissato dallo stack non compare
   * nemmeno il campo con cui, cambiandolo, si ricominciava da capo.
   */
  onRetry?: () => Promise<unknown>;
  /**
   * Scollegarsi e richiedere di nuovo il collegamento.
   *
   * La via d'uscita da un collegamento che non funziona più, senza dover procurarsi un
   * token: il collegamento è un fatto a due lati, e presentarsi di nuovo dice a WatchPOD
   * che da questa parte non c'è più — la richiesta torna fra quelle da approvare. Prima
   * l'unica strada era generare un token dal pannello e incollarlo qui, che con WatchPOD
   * non a portata (o senza nessuno davanti allo schermo) non era una strada.
   */
  onUnpair?: () => Promise<unknown>;
  onRefresh?: () => void | Promise<unknown>;
  envHint?: string;
}) {
  const s = useS(ui);
  const fmt = ui.formatDate ?? defaultDate;
  const [busy, setBusy] = useState(false);
  const [restarting, setRestarting] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [url, setUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  // «Controlla» parlava al server e non lo diceva a nessuno: nessun pulsante spento,
  // nessuna scritta, e chi premeva non sapeva se fosse successo qualcosa.
  const [checking, setChecking] = useState(false);

  if (restarting !== null) {
    return (
      <UpdatingOverlay
        ui={ui}
        version={overview?.latest ?? ""}
        elapsed={restarting}
        onReload={() => void actions.reload()}
      />
    );
  }
  if (!overview) return null;
  const d = overview;
  // Il riquadro del token e' un ricovero per un collegamento che non c'e' piu', non la
  // reazione a un errore qualunque: un timeout mentre il collegamento e' attivo faceva
  // spuntare dal nulla un campo «incolla qui il token» in una scheda che funzionava.
  const troubled = d.pairing !== "active";
  // L'indirizzo si chiede solo se l'host sa salvarlo e lo stack non lo impone gia'.
  const canAskForUrl = !!onSaveUrl && !d.urlPinned && d.pairing !== "active";

  const install = async () => {
    if (
      !confirm(
        `Installare la versione ${d.latest}? L'app si riavvia: circa un minuto di indisponibilità.`,
      )
    )
      return;
    setBusy(true);
    setOutcome(null);
    try {
      const accepted = await actions.install(d.latest);
      setRestarting(0);
      const back = await actions.waitForRestart(accepted.version || d.latest, setRestarting);
      if (back) await actions.reload();
      else {
        setRestarting(null);
        setOutcome("Il riavvio sta impiegando più del previsto: controlla lo storico su WatchPOD.");
      }
    } catch (e) {
      setOutcome(e instanceof Error ? e.message : "Aggiornamento non riuscito.");
    } finally {
      setBusy(false);
    }
  };
  // Finche' non si tocca il campo mostra l'indirizzo corrente: cosi' correggere un
  // refuso non obbliga a riscriverlo da capo.
  const urlValue = urlTouched ? url : (overview?.url ?? "");
  const saveUrl = async () => {
    if (!onSaveUrl) return;
    setBusy(true);
    setOutcome(null);
    try {
      await onSaveUrl(urlValue.trim());
      setUrlTouched(false);
      setOutcome("Richiesta inviata: approvala dal pannello di WatchPOD.");
      onRefresh?.();
    } catch (e) {
      setOutcome(e instanceof Error ? e.message : "Indirizzo non accettato.");
    } finally {
      setBusy(false);
    }
  };
  const check = async () => {
    setChecking(true);
    setOutcome(null);
    try {
      await onRefresh?.();
    } finally {
      setChecking(false);
    }
  };

  const retry = async () => {
    if (!onRetry) return;
    setBusy(true);
    setOutcome(null);
    try {
      await onRetry();
      setOutcome("Richiesta inviata: approvala dal pannello di WatchPOD.");
      await onRefresh?.();
    } catch (e) {
      setOutcome(e instanceof Error ? e.message : "Richiesta non inviata.");
    } finally {
      setBusy(false);
    }
  };

  const unpair = async () => {
    if (!onUnpair) return;
    setBusy(true);
    setOutcome(null);
    try {
      await onUnpair();
      setOutcome("Richiesta inviata: approvala dal pannello di WatchPOD.");
      await onRefresh?.();
    } catch (e) {
      setOutcome(e instanceof Error ? e.message : "Richiesta non inviata.");
    } finally {
      setBusy(false);
    }
  };

  const saveToken = async () => {
    if (!onSaveToken) return;
    setBusy(true);
    setOutcome(null);
    try {
      await onSaveToken(token);
      setToken("");
      setOutcome("Collegamento ripristinato.");
      onRefresh?.();
    } catch (e) {
      setOutcome(e instanceof Error ? e.message : "Token non accettato.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {ui.appName} {d.current}
            {d.updateAvailable && (
              <span style={{ fontWeight: 400, opacity: 0.7 }}> → disponibile la {d.latest}</span>
            )}
          </div>
          <div className={ui.classNames?.muted} style={s("muted")}>
            {d.pairing === "active"
              ? d.updateAvailable
                ? `Rilasciata il ${d.releasedAt ? fmt(d.releasedAt) : "—"}`
                : "Nessun aggiornamento in attesa."
              : PAIRING_LABEL[d.pairing]}
          </div>
        </div>
        {d.pairing === "active" && d.updateAvailable && (
          <button
            type="button"
            className={ui.classNames?.buttonPrimary}
            style={s("buttonPrimary")}
            disabled={busy}
            onClick={() => void install()}
          >
            {busy ? "Aggiornamento…" : `Aggiorna a ${d.latest}`}
          </button>
        )}
        {onRetry && d.pairing === "rejected" && (
          <button
            type="button"
            className={ui.classNames?.buttonPrimary}
            style={s("buttonPrimary")}
            disabled={busy}
            onClick={() => void retry()}
          >
            {busy ? "Invio…" : "Richiedi di nuovo"}
          </button>
        )}
        {onRefresh && (
          <button
            type="button"
            className={ui.classNames?.button}
            style={s("button")}
            disabled={busy || checking}
            onClick={() => void check()}
          >
            {checking ? "Controllo…" : "Controlla"}
          </button>
        )}
      </div>
      {d.notes && d.updateAvailable && (
        <div
          className={ui.classNames?.notes}
          style={{
            ...s("notes"),
            whiteSpace: "normal",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 600 }}>Novità della {d.latest}</div>
          <NotesGroups groups={parseNotesBody(d.notes)} />
        </div>
      )}
      {d.snoozed && d.snoozed === d.latest && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          Hai rimandato la {d.snoozed}: non ti viene più proposta, ma da qui puoi installarla quando
          vuoi.
        </div>
      )}
      {d.pairing === "pending" && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          {ui.appName} si è presentato a WatchPOD e aspetta il via libera: approvalo dal pannello
          confermando repo, stack e immagine. Da lì in poi non serve toccare nulla.
          {d.userCode ? (
            <>
              {" "}
              Nel pannello la richiesta porta il codice <strong>{d.userCode}</strong>: approva
              quella con questo codice.
            </>
          ) : null}
        </div>
      )}
      {d.pairing === "rejected" && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          Il collegamento è stato rifiutato dal pannello di WatchPOD, e da allora {ui.appName} non
          insiste più. Per riprovare, rimetti la richiesta in attesa da lì — poi «Richiedi di nuovo»
          qui sotto.
        </div>
      )}
      {d.pairing === "unpaired" && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          WatchPOD considera attiva questa istanza ma non ha consegnato il token. Genera un token
          dal pannello WatchPOD e inseriscilo qui sotto per verificare e ripristinare il
          collegamento.
        </div>
      )}
      {d.pairing === "not_configured" && !canAskForUrl && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          Aggiornamenti non attivi: manca <strong>{envHint}</strong> nelle variabili dello stack.
        </div>
      )}
      {canAskForUrl && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            border: "1px solid rgba(15,23,42,.1)",
            borderRadius: 12,
            padding: "12px 16px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600 }}>Associa WatchPOD</div>
          <div className={ui.classNames?.muted} style={s("muted")}>
            Indica dove ascolta WatchPOD sulla rete locale. {ui.appName} si presenta da solo, e il
            collegamento diventa attivo quando lo approvi dal pannello di WatchPOD confermando repo,
            stack e immagine.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={urlValue}
              onChange={(e) => {
                setUrlTouched(true);
                setUrl(e.target.value);
              }}
              placeholder="http://watchpod:8080"
              className={ui.classNames?.input}
              style={{ flex: 1, ...s("input") }}
            />
            <button
              type="button"
              className={ui.classNames?.buttonPrimary}
              style={s("buttonPrimary")}
              disabled={busy || urlValue.trim() === ""}
              onClick={() => void saveUrl()}
            >
              {busy ? "Invio…" : "Richiedi collegamento"}
            </button>
          </div>
        </div>
      )}
      {/* **Anche da collegati**, e non solo quando qualcosa non va.
          Il collegamento e' un fatto a due lati: chi amministra il pannello puo' eliminare
          l'app in qualunque momento, e da questa parte resta uno stato «attivo» con un
          token che non vale piu' — senza nessun pulsante per uscirne. L'unica strada era
          aspettare che una chiamata prendesse un 401, e se nel frattempo non se ne fa
          nessuna non succede mai: un limbo, e per uscirne bisognava mettere le mani nel
          volume.
          Nulla da sciogliere, invece, dove un collegamento non c'e' ancora: con
          `not_configured` non e' stato indicato nemmeno un indirizzo, e con `pending` la
          richiesta e' in coda — «scollegarsi» da una richiesta in attesa butta il segreto
          con cui si ritirera' il token, cioe' rimette l'app in fondo alla fila per niente. */}
      {onUnpair &&
        d.pairing !== "pending" &&
        d.pairing !== "not_configured" &&
        d.pairing !== "unpaired" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              border: "1px solid rgba(15,23,42,.1)",
              borderRadius: 12,
              padding: "12px 16px",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>Ricollega {ui.appName}</div>
            <div className={ui.classNames?.muted} style={s("muted")}>
              Il collegamento è un fatto a due lati: da qui {ui.appName} butta il suo token e si
              ripresenta, e WatchPOD rimette la richiesta fra quelle da approvare. Non serve
              procurarsi niente — basta approvarla dal pannello, come la prima volta.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className={ui.classNames?.buttonPrimary}
                style={s("buttonPrimary")}
                disabled={busy}
                onClick={() => void unpair()}
              >
                {busy ? "Invio…" : "Scollega e richiedi di nuovo"}
              </button>
            </div>
          </div>
        )}
      {troubled && onSaveToken && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            border: "1px solid rgba(15,23,42,.1)",
            borderRadius: 12,
            padding: "12px 16px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600 }}>Token di WatchPOD</div>
          <div className={ui.classNames?.muted} style={s("muted")}>
            Ripiego, se il collegamento non riparte: genera un token dal pannello di WatchPOD —
            pulsante «Token» sulla riga di {ui.appName} — e incollalo qui.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="incolla qui il token"
              className={ui.classNames?.input}
              style={{ flex: 1, fontSize: 13, ...s("input") }}
            />
            <button
              type="button"
              className={ui.classNames?.buttonPrimary}
              style={s("buttonPrimary")}
              disabled={!token.trim() || busy}
              onClick={() => void saveToken()}
            >
              {busy ? "Verifica…" : "Salva"}
            </button>
          </div>
        </div>
      )}
      {(outcome || d.lastError) && (
        <div className={ui.classNames?.muted} style={s("muted")}>
          {outcome ?? d.lastError}
        </div>
      )}
    </div>
  );
}

/** Cadenze proposte: sotto i 5 minuti si interroga WatchPOD senza motivo. */
export const CHECK_INTERVALS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 5, label: "Ogni 5 minuti" },
  { value: 15, label: "Ogni 15 minuti" },
  { value: 30, label: "Ogni 30 minuti" },
  { value: 60, label: "Ogni ora" },
  { value: 360, label: "Ogni 6 ore" },
  { value: 1440, label: "Una volta al giorno" },
];

/**
 * I testi della sezione.
 *
 * L'italiano e' il valore predefinito perche' e' la lingua di questa flotta, ma una
 * dashboard tradotta passa i propri: senza questo, adottare la sezione condivisa avrebbe
 * significato perdere l'inglese dove c'era.
 */
export type UpdatesSettingsLabels = {
  title: string;
  description: ReactNode;
  autoTitle: string;
  autoHelp: string;
  cadenceTitle: string;
  cadenceHelp: string;
  noticeTitle: string;
  noticeHelp: string;
  soundTitle: string;
  soundHelp: string;
};

export type UpdatePrefs = {
  /** Installa da se' una release non posticipata, appena la vede. */
  autoUpdate: boolean;
  /** Ogni quanto interrogare WatchPOD, in minuti. */
  checkMinutes: number;
  /** Con quanti minuti di preavviso parte l'aggiornamento automatico (host che lo annuncia). */
  noticeMinutes?: number;
  /** Il conto alla rovescia suona (alla comparsa e ogni minuto). */
  noticeSound?: boolean;
};

export const NOTICE_INTERVALS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: "1 minuto prima" },
  { value: 2, label: "2 minuti prima" },
  { value: 5, label: "5 minuti prima" },
  { value: 10, label: "10 minuti prima" },
  { value: 15, label: "15 minuti prima" },
  { value: 20, label: "20 minuti prima" },
  { value: 30, label: "30 minuti prima" },
];

/**
 * La sezione «Aggiornamenti» delle impostazioni, intera.
 *
 * Prima viveva nell'host, riscritta in ogni dashboard: sette impaginazioni diverse per la
 * stessa cosa, e una correzione andava rifatta sette volte. Qui c'e' una volta sola —
 * intestazione, le due preferenze, la scheda del collegamento — e ogni host la veste con
 * `ui.styles`/`ui.classNames` come veste il resto delle sue impostazioni.
 *
 * L'ordine racconta la sequenza: finche' il collegamento non e' attivo si chiede
 * l'indirizzo di WatchPOD (dentro la scheda) e non si mostra nient'altro, perche'
 * «ogni quanto controllare» e «installa da solo» sono impostazioni senza oggetto per un
 * host che non e' collegato a niente. Approvato il collegamento, al loro posto compaiono.
 */
export function UpdatesSettings({
  ui,
  overview,
  actions,
  prefs,
  onSavePrefs,
  onSaveToken,
  onSaveUrl,
  onRetry,
  onUnpair,
  onRefresh,
  labels,
  intervals = CHECK_INTERVALS,
  noticeIntervals = NOTICE_INTERVALS,
  busy: busyProp = false,
  renderSelect,
  envHint,
  changelog,
  appVersion,
  notesLabel,
}: {
  ui: UiProps;
  overview: (UpdateOverview & Partial<UpdatePrefs>) | null;
  actions: UpdateActions;
  /**
   * Il CHANGELOG.md dell'host e la versione che gira (0.17.0): con tutti e due, in fondo alla
   * scheda compare «Novità della versione X», che apre `ReleaseNotesModal`. Cosi' la pagina
   * Aggiornamenti si incolla intera in un'altra dashboard, note comprese.
   */
  changelog?: string;
  appVersion?: string;
  /** Il testo del pulsante delle note; predefinito «Novità della versione X». */
  notesLabel?: string;
  /** Sovrascrive i valori presi da `overview`, per chi tiene le preferenze altrove. */
  prefs?: UpdatePrefs;
  /** Assente = preferenze in sola lettura (chi guarda non e' amministratore). */
  onSavePrefs?: (patch: Partial<UpdatePrefs>) => Promise<unknown> | unknown;
  onSaveToken?: (token: string) => Promise<unknown>;
  onSaveUrl?: (url: string) => Promise<unknown>;
  onRetry?: () => Promise<unknown>;
  /** Scollegarsi e richiedere di nuovo il collegamento: vedi `UpdatesCard`. */
  onUnpair?: () => Promise<unknown>;
  onRefresh?: () => void | Promise<unknown>;
  /** Solo le voci da tradurre: le altre restano quelle predefinite. */
  labels?: Partial<UpdatesSettingsLabels>;
  intervals?: ReadonlyArray<{ value: number; label: string }>;
  /** Le scelte del preavviso, in minuti. */
  noticeIntervals?: ReadonlyArray<{ value: number; label: string }>;
  busy?: boolean;
  /**
   * Il menu a tendina dell'host, quando ne ha uno suo.
   *
   * Un `<select>` nativo non si veste come gli altri campi di una dashboard che ha il
   * proprio componente: chi ce l'ha lo passa qui e la riga resta la sua, invece di essere
   * l'unico controllo fuori posto della pagina.
   */
  renderSelect?: (props: {
    value: string;
    options: ReadonlyArray<{ value: number; label: string }>;
    disabled: boolean;
    onChange: (minutes: number) => void;
  }) => ReactNode;
  /**
   * Come si chiama, in *questo* host, la variabile che fissa l'indirizzo.
   *
   * La scheda la nomina quando l'indirizzo arriva dallo stack — e' l'unica informazione
   * utile a chi vuole cambiarlo — ma il nome non e' lo stesso ovunque: `MEMO_WATCHPOD_URL`,
   * `STK_WATCHPOD_URL`. Mandare a cercare una variabile che non esiste e' peggio che tacere.
   */
  envHint?: string;
}) {
  // a host that gives its own muted colour keeps it as it is: the base opacity on top of an already
  // soft grey left the help text below 4.5:1 (SurgeAI, 2026-10-30)
  const s = (k: StyleKeys): CSSProperties => ({
    ...base[k],
    ...(k === "muted" && ui.styles?.muted?.color ? { opacity: 1 } : {}),
    ...(ui.styles?.[k] ?? {}),
  });
  const [notesOpen, setNotesOpen] = useState(false);
  const text: UpdatesSettingsLabels = {
    title: "Aggiornamenti",
    description: (
      <>
        {ui.appName} chiede a WatchPOD se esiste una release più recente e la installa facendo
        ridispiegare lo stack. Durante l'aggiornamento è indisponibile per circa un minuto.
      </>
    ),
    autoTitle: "Aggiorna automaticamente",
    autoHelp: "Installa da sé le nuove release, senza chiedere. Quelle posticipate restano ferme.",
    cadenceTitle: "Frequenza del controllo",
    cadenceHelp: "Ogni quanto chiedere a WatchPOD.",
    noticeTitle: "Preavviso",
    noticeHelp: "Prima di aggiornarsi da sé avvisa tutte le postazioni con un conto alla rovescia.",
    soundTitle: "Suono del preavviso",
    soundHelp: "Un bip quando compare il conto alla rovescia, poi uno ogni minuto.",
    ...labels,
  };
  const autoUpdate = prefs?.autoUpdate ?? overview?.autoUpdate ?? false;
  const checkMinutes = prefs?.checkMinutes ?? overview?.checkMinutes ?? 30;
  // Solo per gli host che annunciano l'aggiornamento: senza il campo la riga non c'e'.
  const noticeMinutes = prefs?.noticeMinutes ?? overview?.noticeMinutes;
  const noticeSound = prefs?.noticeSound ?? overview?.noticeSound;
  /**
   * Quando ha senso mostrare «ogni quanto» e «installa da solo».
   *
   * Non solo a collegamento attivo: un'app in attesa di approvazione, o con il token da
   * rimettere, e' gia' associata a un WatchPOD — la cadenza governa anche il giro che
   * cerca l'approvazione, e nasconderla proprio mentre qualcosa non va lascia senza
   * comandi chi sta cercando di sistemare. Si nasconde solo dove davvero non c'e' niente
   * da regolare: quando non e' stato ancora indicato nessun indirizzo.
   */
  const configured = !!overview && overview.pairing !== "not_configured";
  // Le due preferenze si mostrano solo se l'host le ha davvero: chi espone solo lo stato
  // del collegamento (nessun `autoUpdate`, nessun `checkMinutes`) vedrebbe altrimenti due
  // controlli che il suo backend non sa salvare.
  const hasPrefs =
    prefs !== undefined ||
    overview?.autoUpdate !== undefined ||
    overview?.checkMinutes !== undefined;
  const editable = !!onSavePrefs && !busyProp && !!overview;

  const toggleStyle: CSSProperties = {
    ...s("toggle"),
    ...(autoUpdate ? s("toggleOn") : {}),
    cursor: editable ? "pointer" : "default",
  };

  return (
    <div className={ui.classNames?.section} style={s("section")}>
      <div style={{ paddingBottom: 14 }}>
        <div className={ui.classNames?.sectionTitle} style={s("sectionTitle")}>
          {text.title}
        </div>
        <div className={ui.classNames?.muted} style={{ ...s("muted"), marginTop: 4 }}>
          {text.description}
        </div>
      </div>

      {configured && hasPrefs && (
        <>
          <div className={ui.classNames?.row} style={s("row")}>
            <div>
              <div className={ui.classNames?.rowTitle} style={s("rowTitle")}>
                {text.autoTitle}
              </div>
              <div className={ui.classNames?.muted} style={s("muted")}>
                {text.autoHelp}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoUpdate}
              aria-label={text.autoTitle}
              disabled={!editable}
              onClick={() => void onSavePrefs?.({ autoUpdate: !autoUpdate })}
              style={toggleStyle}
            >
              <span style={{ ...s("knob"), left: autoUpdate ? 22 : 3 }} />
            </button>
          </div>

          <div className={ui.classNames?.row} style={s("row")}>
            <div>
              <div className={ui.classNames?.rowTitle} style={s("rowTitle")}>
                {text.cadenceTitle}
              </div>
              <div className={ui.classNames?.muted} style={s("muted")}>
                {text.cadenceHelp}
              </div>
            </div>
            {renderSelect ? (
              renderSelect({
                value: String(checkMinutes),
                options: intervals,
                disabled: !editable,
                onChange: (minutes) => void onSavePrefs?.({ checkMinutes: minutes }),
              })
            ) : (
              <Dropdown
                label={text.cadenceTitle}
                disabled={!editable}
                value={String(checkMinutes)}
                options={intervals}
                onChange={(minutes) => void onSavePrefs?.({ checkMinutes: minutes })}
                className={ui.classNames?.select}
                style={s("select")}
                menuStyle={s("menu")}
              />
            )}
          </div>

          {noticeMinutes !== undefined && (
            <div className={ui.classNames?.row} style={s("row")}>
              <div>
                <div className={ui.classNames?.rowTitle} style={s("rowTitle")}>
                  {text.noticeTitle}
                </div>
                <div className={ui.classNames?.muted} style={s("muted")}>
                  {text.noticeHelp}
                </div>
              </div>
              {renderSelect ? (
                renderSelect({
                  value: String(noticeMinutes),
                  options: noticeIntervals,
                  disabled: !editable,
                  onChange: (minutes) => void onSavePrefs?.({ noticeMinutes: minutes }),
                })
              ) : (
                <Dropdown
                  label={text.noticeTitle}
                  disabled={!editable}
                  value={String(noticeMinutes)}
                  options={noticeIntervals}
                  onChange={(minutes) => void onSavePrefs?.({ noticeMinutes: minutes })}
                  className={ui.classNames?.select}
                  style={s("select")}
                  menuStyle={s("menu")}
                />
              )}
            </div>
          )}

          {noticeSound !== undefined && (
            <div className={ui.classNames?.row} style={s("row")}>
              <div>
                <div className={ui.classNames?.rowTitle} style={s("rowTitle")}>
                  {text.soundTitle}
                </div>
                <div className={ui.classNames?.muted} style={s("muted")}>
                  {text.soundHelp}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={noticeSound}
                aria-label={text.soundTitle}
                disabled={!editable}
                onClick={() => void onSavePrefs?.({ noticeSound: !noticeSound })}
                style={{
                  ...s("toggle"),
                  ...(noticeSound ? s("toggleOn") : {}),
                  cursor: editable ? "pointer" : "default",
                }}
              >
                <span style={{ ...s("knob"), left: noticeSound ? 22 : 3 }} />
              </button>
            </div>
          )}
        </>
      )}

      <div className={ui.classNames?.row} style={{ ...s("row"), display: "block" }}>
        <UpdatesCard
          ui={ui}
          overview={overview}
          actions={actions}
          onSaveToken={onSaveToken}
          onSaveUrl={onSaveUrl}
          onRetry={onRetry}
          onUnpair={onUnpair}
          onRefresh={onRefresh}
          {...(envHint ? { envHint } : {})}
        />
      </div>

      {changelog !== undefined && appVersion && (
        <div className={ui.classNames?.row} style={{ ...s("row"), justifyContent: "flex-start" }}>
          <button
            type="button"
            className={ui.classNames?.buttonPrimary}
            style={s("buttonPrimary")}
            onClick={() => setNotesOpen(true)}
          >
            {notesLabel ?? `Novità della versione ${appVersion}`}
          </button>
        </div>
      )}
      {notesOpen && changelog !== undefined && appVersion && (
        <ReleaseNotesModal
          ui={ui}
          changelog={changelog}
          appVersion={appVersion}
          eyebrow="Note di rilascio"
          onClose={() => setNotesOpen(false)}
        />
      )}
    </div>
  );
}
